import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Search, FileText, Download, Trash2, Shield, RefreshCw, Upload, 
  CheckSquare, Square, ChevronRight, Briefcase, Home, Car, MoreHorizontal, 
  Lock, ShieldCheck, MousePointer2, ArrowUpRight, Send, X, Edit2, Archive, Loader2,
  FileImage, TrendingUp, Filter, AlertCircle, Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster'; 
import { apiService } from '../services/api';
import EditDocumentModal from '../components/EditDocumentModal';
import HeaderActions from '../components/HeaderActions'; 
import Footer from '../components/Footer';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const ArchivePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage(); 
  
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all'); 
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [previewDoc, setPreviewDoc] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Kategori Konfigürasyonu (İkon ve Renkler - Daha Canlı)
  const CATEGORY_MAP = {
    tax: { icon: FileText, color: "text-blue-400", bg: "bg-blue-500/20", border: "border-blue-500/20" },
    office: { icon: Briefcase, color: "text-purple-400", bg: "bg-purple-500/20", border: "border-purple-500/20" },
    rent: { icon: Home, color: "text-orange-400", bg: "bg-orange-500/20", border: "border-orange-500/20" },
    car: { icon: Car, color: "text-rose-400", bg: "bg-rose-500/20", border: "border-rose-500/20" },
    default: { icon: MoreHorizontal, color: "text-emerald-400", bg: "bg-emerald-500/20", border: "border-emerald-500/20" }
  };

  const getCategoryLabel = (key) => {
    const cleanKey = (key || '').toLowerCase().replace('category_', '');
    switch(cleanKey) {
        case 'tax': return t('category_tax') || 'Tax';
        case 'office': return t('category_office') || 'Office';
        case 'housing': case 'rent': return t('category_housing') || 'Housing';
        case 'car': return t('category_car') || 'Vehicle';
        case 'insurance': return t('category_insurance') || 'Insurance';
        default: return t('category_general') || 'General';
    }
  };

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiService.documents.getAll();
      const docs = response.data || [];
      setDocuments(docs);
      if (docs.length > 0) setPreviewDoc(docs[0]); 
    } catch (err) {
      console.error("Fetch Error:", err);
      // Fallback Demo Data (Görsel Zenginlik İçin Çeşitlendirildi)
      const demoDocs = [
          { id: 'doc-1', titleKey: 'MacBook Pro M3 Max', merchant_name: 'Apple Store Frankfurt', amount: 3499.00, date: '2025-10-24', category: 'office', type: 'pdf', status: 'verified' },
          { id: 'doc-2', titleKey: 'Aral Tankstelle', merchant_name: 'Aral AG', amount: 85.50, date: '2025-10-22', category: 'car', type: 'img', status: 'pending' },
          { id: 'doc-3', titleKey: 'Haftpflichtversicherung', merchant_name: 'Allianz', amount: 124.90, date: '2025-10-20', category: 'insurance', type: 'pdf', status: 'verified' },
          { id: 'doc-4', titleKey: 'Miete November', merchant_name: 'Vonovia SE', amount: 1450.00, date: '2025-11-01', category: 'rent', type: 'pdf', status: 'verified' },
          { id: 'doc-5', titleKey: 'Business Lunch', merchant_name: 'Vapiano', amount: 42.80, date: '2025-10-18', category: 'office', type: 'img', status: 'warning' },
      ];
      setDocuments(demoDocs);
      setPreviewDoc(demoDocs[0]);
    } finally { setLoading(false); }
  };

  const getCategoryConfig = (cat) => {
    const key = (cat || 'default').toLowerCase().replace(/^(category_)+/, '');
    if (['tax', 'office', 'rent', 'car', 'insurance'].includes(key)) return CATEGORY_MAP[key] || CATEGORY_MAP.default;
    return CATEGORY_MAP.default;
  };

  const filteredDocs = useMemo(() => {
    return documents.filter(doc => {
        const matchesSearch = (doc.merchant_name || doc.titleKey || "").toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || (doc.category || 'default').toLowerCase().includes(selectedCategory.toLowerCase());
        return matchesSearch && matchesCategory;
    }).sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
  }, [documents, searchQuery, selectedCategory]);

  // İstatistikler (Filtrelenen Belgelere Göre)
  const stats = useMemo(() => {
      const total = filteredDocs.reduce((sum, doc) => sum + (Number(doc.amount) || 0), 0);
      const count = filteredDocs.length;
      return { total, count };
  }, [filteredDocs]);

  const toggleSelection = (id) => {
    const newSet = new Set(selectedDocIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedDocIds(newSet);
  };

  const selectAll = () => {
    if (selectedDocIds.size === filteredDocs.length) setSelectedDocIds(new Set());
    else setSelectedDocIds(new Set(filteredDocs.map(d => d.id)));
  };

  const handleBulkAction = (action) => {
    toast({ 
        title: t('success_general') || "Success", 
        description: `${selectedDocIds.size} files processed for ${action}.`,
        className: "bg-blue-600 text-white border-0" 
    });
    setSelectedDocIds(new Set());
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-sans pb-32 transition-colors duration-500 flex flex-col relative overflow-x-hidden">
      <Toaster />
      
      {/* Background Ambience */}
      <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px]"></div>
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4 transition-all duration-300">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.scrollTo({top:0, behavior:'smooth'})}>
                <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl text-white shadow-lg shadow-blue-600/20 ring-1 ring-white/10">
                    <Archive size={20} strokeWidth={2.5} />
                </div>
                <div>
                    <h1 className="text-lg font-black tracking-tight text-white uppercase flex items-center gap-2 leading-none">
                        DIGITAL <span className="text-blue-400">VAULT</span>
                    </h1>
                    <div className="flex items-center gap-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                        <span className="flex items-center gap-1 text-emerald-400"><ShieldCheck size={10} /> {t('encrypted_analysis') || 'Encrypted'}</span>
                        <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                        <span>{documents.length} Files</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4">
                 <div className="hidden md:flex relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-400 transition-colors" size={16} />
                    <Input 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('search_procedures') || 'Search archive...'} 
                        className="pl-10 h-10 bg-[#111827]/50 border-white/10 rounded-xl w-64 focus:border-blue-500 transition-all font-medium text-xs shadow-inner text-white placeholder:text-slate-500 focus:bg-[#111827] focus:ring-1 focus:ring-blue-500/50"
                    />
                 </div>
                 
                 <Button onClick={() => fileInputRef.current?.click()} className="h-10 bg-white text-[#0B1120] hover:bg-slate-200 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg transition-all hidden sm:flex active:scale-95">
                    <Upload size={14} className="mr-2" /> {t('action_upload') || 'Upload'}
                 </Button>
                 <input type="file" ref={fileInputRef} className="hidden" />

                 <HeaderActions />
            </div>
        </div>
      </header>

      {/* FILTER & STATS BAR */}
      <div className="border-b border-white/5 bg-[#0B1120]/40 backdrop-blur-md sticky top-[73px] z-30">
          <div className="max-w-[1600px] mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-4">
               
               {/* CATEGORY TABS */}
               <div className="flex items-center gap-1 p-1 bg-white/5 rounded-xl border border-white/5 overflow-x-auto no-scrollbar max-w-full">
                   {[
                       { id: 'all', label: t('tab_all') || 'All' },
                       { id: 'tax', label: t('category_tax') || 'Tax' },
                       { id: 'office', label: t('category_office') || 'Office' },
                       { id: 'rent', label: t('category_housing') || 'Housing' },
                       { id: 'car', label: t('category_car') || 'Vehicle' }
                   ].map(cat => (
                       <button
                           key={cat.id}
                           onClick={() => setSelectedCategory(cat.id)}
                           className={`relative px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all z-10 ${
                               selectedCategory === cat.id ? 'text-white' : 'text-slate-400 hover:text-white'
                           }`}
                       >
                           {selectedCategory === cat.id && (
                               <motion.div 
                                 layoutId="activeTab"
                                 className="absolute inset-0 bg-blue-600 rounded-lg -z-10 shadow-lg shadow-blue-900/50"
                                 transition={{ type: "spring", stiffness: 300, damping: 30 }}
                               />
                           )}
                           {cat.label}
                       </button>
                   ))}
               </div>

               {/* QUICK STATS */}
               <div className="flex items-center gap-6 text-xs font-medium text-slate-400 bg-white/[0.02] px-4 py-2 rounded-xl border border-white/5">
                   <div className="flex items-center gap-2">
                       <Filter size={12} className="text-blue-400" />
                       <span className="font-bold text-slate-200">{filteredDocs.length}</span> <span className="uppercase text-[9px] tracking-wider">Items</span>
                   </div>
                   <div className="w-px h-3 bg-white/10"></div>
                   <div className="flex items-center gap-2">
                       <TrendingUp size={12} className="text-emerald-400" />
                       <span className="font-bold text-slate-200">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(stats.total)}</span> <span className="uppercase text-[9px] tracking-wider">Total</span>
                   </div>
               </div>
          </div>
      </div>

      <main className="flex-1 max-w-[1600px] mx-auto w-full px-6 py-6 overflow-hidden flex flex-col md:flex-row gap-6 relative z-10">
          
          {/* LEFT: DOCUMENT LIST */}
          <div className="flex-1 flex flex-col h-full overflow-hidden glass-panel rounded-[2rem] shadow-2xl border border-white/5 bg-[#111827]/40">
              
              {/* List Header */}
              <div className="flex items-center px-6 py-4 border-b border-white/5 bg-white/[0.02]">
                  <div className="w-8">
                      <button onClick={selectAll} className="text-slate-500 hover:text-blue-400 transition-colors">
                          {selectedDocIds.size === filteredDocs.length && filteredDocs.length > 0 ? <CheckSquare size={18} className="text-blue-500"/> : <Square size={18} />}
                      </button>
                  </div>
                  <div className="flex-1 text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">{t('doc_name') || 'Document'}</div>
                  <div className="w-24 text-[10px] font-black text-slate-500 uppercase tracking-widest hidden sm:block">{t('category') || 'Category'}</div>
                  <div className="w-24 text-[10px] font-black text-slate-500 uppercase tracking-widest hidden md:block">{t('date') || 'Date'}</div>
                  <div className="w-28 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('amount') || 'Value'}</div>
                  <div className="w-8"></div>
              </div>

              {/* Scrollable List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                  {loading ? (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-500 gap-4">
                          <div className="relative">
                              <div className="w-12 h-12 rounded-full border-4 border-blue-500/30 border-t-blue-500 animate-spin"></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              </div>
                          </div>
                          <p className="text-[10px] font-black uppercase tracking-[0.2em]">{t('loading') || 'Accessing Vault...'}</p>
                      </div>
                  ) : (
                      <AnimatePresence>
                      {filteredDocs.map((doc) => {
                          const config = getCategoryConfig(doc.category);
                          const isSelected = selectedDocIds.has(doc.id);
                          const isPreviewing = previewDoc?.id === doc.id;

                          return (
                              <motion.div 
                                  key={doc.id}
                                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                  onClick={() => setPreviewDoc(doc)}
                                  className={`group flex items-center px-4 py-3.5 rounded-xl border transition-all cursor-pointer relative overflow-hidden ${
                                      isPreviewing 
                                      ? 'bg-blue-500/10 border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.15)]' 
                                      : 'bg-transparent border-transparent hover:bg-white/5 hover:border-white/5'
                                  }`}
                              >
                                  {/* Selection Checkbox */}
                                  <div className="w-8 flex items-center relative z-10" onClick={(e) => { e.stopPropagation(); toggleSelection(doc.id); }}>
                                      <div className={`transition-colors ${isSelected ? 'text-blue-500' : 'text-slate-600 group-hover:text-slate-400'}`}>
                                          {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                                      </div>
                                  </div>

                                  {/* Icon & Name */}
                                  <div className="flex-1 flex items-center gap-4 ml-4 relative z-10">
                                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${config.bg} ${config.color} shadow-sm border ${config.border} group-hover:scale-105 transition-transform duration-300`}>
                                          {doc.type === 'img' ? <FileImage size={20} /> : <FileText size={20} />}
                                      </div>
                                      <div>
                                          <div className={`text-sm font-bold ${isPreviewing ? 'text-white' : 'text-slate-200'} truncate max-w-[150px] sm:max-w-xs transition-colors`}>
                                              {doc.merchant_name || doc.titleKey || t('untitled_doc') || "Unknown Document"}
                                          </div>
                                          <div className="flex items-center gap-2 mt-0.5">
                                              <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-black/20 px-1.5 py-0.5 rounded-md border border-white/5">
                                                  {doc.type === 'img' ? 'IMG' : 'PDF'}
                                              </span>
                                              {doc.status === 'warning' && (
                                                  <span className="flex items-center gap-1 text-[9px] text-orange-400 font-bold uppercase"><AlertCircle size={10} /> Check</span>
                                              )}
                                          </div>
                                      </div>
                                  </div>

                                  {/* Category Badge */}
                                  <div className="w-24 hidden sm:block relative z-10">
                                      <span className={`text-[9px] font-bold px-2.5 py-1 rounded-lg ${config.bg} ${config.color} border ${config.border} uppercase tracking-wider`}>
                                          {getCategoryLabel(doc.category)}
                                      </span>
                                  </div>

                                  {/* Date */}
                                  <div className="w-24 text-xs font-medium text-slate-500 hidden md:block relative z-10">
                                      {new Date(doc.date).toLocaleDateString()}
                                  </div>

                                  {/* Amount */}
                                  <div className="w-28 text-right font-mono font-bold text-white relative z-10">
                                      {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(doc.amount || 0)}
                                  </div>

                                  {/* Arrow */}
                                  <div className="w-8 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity relative z-10">
                                      <ChevronRight size={16} className="text-slate-500 group-hover:text-white" />
                                  </div>
                                  
                                  {/* Active Indicator Bar */}
                                  {isPreviewing && (
                                      <motion.div layoutId="activeRow" className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                                  )}
                              </motion.div>
                          );
                      })}
                      </AnimatePresence>
                  )}
                  
                  {!loading && filteredDocs.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-24 text-slate-600">
                          <div className="p-6 bg-white/5 rounded-full mb-4 border border-white/5">
                              <Shield size={32} className="opacity-40" />
                          </div>
                          <p className="text-xs uppercase tracking-[0.2em] font-bold">{t('no_documents_yet') || 'No Records Found'}</p>
                          <p className="text-[10px] text-slate-500 mt-2 max-w-xs text-center">Your vault is empty for this category. Upload a document to see it here.</p>
                      </div>
                  )}
              </div>
          </div>

          {/* RIGHT: DETAILED PREVIEW */}
          <div className="hidden lg:flex w-[480px] flex-col h-full glass-panel rounded-[2rem] shadow-2xl overflow-hidden sticky top-6 border border-white/5 bg-[#111827]/60 backdrop-blur-xl">
              {previewDoc ? (
                  <div className="flex flex-col h-full">
                      {/* Preview Header (Ticket Style) */}
                      <div className="p-8 border-b border-white/5 bg-white/[0.02] relative overflow-hidden">
                           <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/10 rounded-full blur-[60px] pointer-events-none"></div>
                           <div className="absolute top-[-10px] left-1/2 -translate-x-1/2 w-20 h-1 bg-blue-500 rounded-full shadow-[0_0_10px_#3b82f6]"></div>

                           <div className="flex justify-between items-start mb-8 relative z-10">
                               <div className={`p-4 rounded-2xl ${getCategoryConfig(previewDoc.category).bg} ${getCategoryConfig(previewDoc.category).color} shadow-lg border border-white/10 ring-1 ring-white/5`}>
                                    {React.createElement(getCategoryConfig(previewDoc.category).icon, { size: 32 })}
                               </div>
                               <div className="flex gap-2">
                                    <button onClick={() => setIsEditModalOpen(true)} className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-slate-400 hover:text-white transition-all shadow-sm group">
                                            <Edit2 size={16} className="group-hover:rotate-12 transition-transform" />
                                    </button>
                               </div>
                           </div>
                           
                           <h2 className="text-3xl font-black text-white leading-none mb-2 uppercase tracking-tight line-clamp-2">{previewDoc.merchant_name || "Unknown"}</h2>
                           
                           <div className="flex items-center gap-4 mt-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
                               <span className="flex items-center gap-1"><Calendar size={12}/> {new Date(previewDoc.date).toLocaleDateString()}</span>
                               <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                               <span>{new Date(previewDoc.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                           </div>
                      </div>

                      {/* Preview Body */}
                      <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                          
                          {/* Total Amount Card */}
                          <div className="bg-gradient-to-r from-blue-600/20 to-indigo-600/20 rounded-2xl p-6 border border-blue-500/20 flex items-center justify-between relative overflow-hidden group">
                              <div className="absolute inset-0 bg-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                              <span className="text-xs font-black uppercase tracking-widest text-blue-300">{t('label_amount') || 'Total Value'}</span>
                              <span className="text-4xl font-mono font-black text-white drop-shadow-lg tabular-nums">
                                  {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(previewDoc.amount || 0)}
                              </span>
                          </div>

                          {/* AI Analysis Summary */}
                          <div className="space-y-4">
                              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                  {t('ai_summary_title') || 'Fox Intelligence'}
                              </h4>
                              <div className="text-sm text-slate-300 leading-relaxed font-medium bg-[#0B1120]/50 p-5 rounded-2xl border border-white/5 shadow-inner">
                                  {previewDoc.descriptionKey || previewDoc.summary || t('no_documents_yet')}
                              </div>
                          </div>

                          {/* Confidence Score Ring (Visual) */}
                          <div className="grid grid-cols-2 gap-4">
                              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 flex flex-col gap-2">
                                  <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider">Confidence</span>
                                  <div className="flex items-center gap-2">
                                      <div className="h-2 flex-1 bg-slate-700 rounded-full overflow-hidden">
                                          <div className="h-full bg-emerald-500 w-[95%]"></div>
                                      </div>
                                      <span className="text-xs font-bold text-emerald-400">95%</span>
                                  </div>
                              </div>
                              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 flex flex-col gap-2">
                                  <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider">Status</span>
                                  <div className="flex items-center gap-2">
                                      <ShieldCheck size={14} className="text-emerald-500"/>
                                      <span className="text-xs font-bold text-white">Verified</span>
                                  </div>
                              </div>
                          </div>

                          {/* Tip Box */}
                          {previewDoc.fox_radar?.action_tip && (
                              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 relative overflow-hidden">
                                  <div className="absolute -right-4 -top-4 text-emerald-500/10 rotate-12"><ShieldCheck size={80}/></div>
                                  <div className="flex items-center gap-2 mb-2 text-emerald-400 relative z-10">
                                      <ShieldCheck size={16} />
                                      <span className="text-[10px] font-black uppercase tracking-widest">Safe Tip</span>
                                  </div>
                                  <p className="text-xs text-emerald-200 font-medium leading-relaxed relative z-10">{previewDoc.fox_radar.action_tip}</p>
                              </div>
                          )}
                      </div>

                      {/* Footer Actions */}
                      <div className="p-6 border-t border-white/5 bg-white/[0.02] flex gap-3">
                          <Button onClick={() => navigate('/analysis-result', { state: { document: previewDoc } })} className="flex-1 bg-white text-[#0B1120] hover:bg-blue-50 font-black uppercase tracking-widest text-[10px] h-12 rounded-xl shadow-lg transition-all active:scale-95">
                              {t('action_view') || 'Full Report'} <ArrowUpRight size={14} className="ml-2"/>
                          </Button>
                          <Button variant="outline" className="h-12 w-12 rounded-xl border-white/10 bg-white/5 hover:bg-white/10 text-white p-0 flex items-center justify-center">
                              <Download size={18} />
                          </Button>
                      </div>
                  </div>
              ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-600 p-8 text-center">
                      <div className="w-32 h-32 bg-white/[0.02] rounded-full flex items-center justify-center mb-6 border border-white/5 animate-pulse shadow-inner">
                          <MousePointer2 size={48} className="opacity-30 text-blue-400" />
                      </div>
                      <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">{t('view_all_documents') || 'Select a File'}</h3>
                      <p className="text-xs text-slate-600 mt-2 font-medium max-w-[200px]">Click on any document from the list to reveal its secure contents.</p>
                  </div>
              )}
          </div>

      </main>
      
      {/* BULK ACTIONS FLOATING BAR */}
      <AnimatePresence>
          {selectedDocIds.size > 0 && (
              <motion.div 
                  initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
                  className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#1e293b] border border-white/10 text-white shadow-2xl shadow-blue-900/50 rounded-2xl px-2 py-2 flex items-center gap-2 ring-1 ring-white/10"
              >
                  <div className="flex items-center gap-3 px-4 py-2 border-r border-white/10">
                      <div className="bg-blue-600 text-white w-6 h-6 rounded flex items-center justify-center text-xs font-bold shadow-md">{selectedDocIds.size}</div>
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-300">{t('selected') || 'Items'}</span>
                  </div>
                  <div className="flex gap-1">
                      <button onClick={() => handleBulkAction('download')} className="p-3 hover:bg-white/10 rounded-xl transition-colors tooltip text-slate-300 hover:text-white group relative" title="Download">
                          <Download size={18} />
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Download</span>
                      </button>
                      <button onClick={() => handleBulkAction('delete')} className="p-3 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-colors tooltip group relative" title="Delete">
                          <Trash2 size={18} />
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Delete</span>
                      </button>
                      <button onClick={() => handleBulkAction('accounting')} className="p-3 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-xl transition-colors tooltip group relative" title="Send to Accountant">
                          <Send size={18} />
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Send</span>
                      </button>
                  </div>
                  <button onClick={() => setSelectedDocIds(new Set())} className="p-3 ml-1 text-slate-500 hover:text-white transition-colors hover:bg-white/5 rounded-xl"><X size={16} /></button>
              </motion.div>
          )}
      </AnimatePresence>

      <EditDocumentModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} document={previewDoc} />
      
      <div className="hidden lg:block absolute bottom-4 right-8 text-right pointer-events-none opacity-30 z-0">
           <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">
             BüroFuchs Intelligence Systems v3.0
           </p>
      </div>
    </div>
  );
};

export default ArchivePage;